import { useSyncExternalStore } from 'react';
import { api } from '@/core/http/apiClient';
import { CookieNames, cookies } from '@/core/cookies';
import { isGuid } from '@/core/utils/guid';

export interface SelectedClient {
  id: string;
  name: string;
}

/**
 * APT244: the cookies stay the one source of truth; this only tells React when they were rewritten. A component that
 * read selectedClient() during render (the Accounts shell's client bar) was not rendered again when the client was
 * switched on a screen inside it, so it kept naming the first client on every screen until a reload.
 */
const listeners = new Set<() => void>();
let snapshot: SelectedClient | null = null;

/** Angular CommonService.setClientId(): replaces the two cookies (delete, then set). */
export function setClientId(clientId: string, clientName?: string | null): void {
  if (clientId) {
    cookies.delete(CookieNames.clientId);
  }
  if (clientName) {
    cookies.delete(CookieNames.clientName);
  }
  cookies.set(CookieNames.clientId, String(clientId));
  if (clientName !== null && clientName !== '' && clientName !== undefined) {
    cookies.set(CookieNames.clientName, String(clientName));
  }
  // APT244: everything showing the selected client (the Accounts shell's client bar above all) follows the switch.
  listeners.forEach((listener) => listener());
}

/** The Accounts screens work on one client at a time; it is remembered in cookies and on the server. */
export function selectClient(id: string, name: string): void {
  setClientId(id, name);
  // Fire and forget: a failure is swallowed.
  api
    .post<boolean>('User/saveLastUsedClient', JSON.stringify(id), { headers: { 'Content-Type': 'application/json' } })
    .catch(() => undefined);
}

/** Read synchronously from the cookies; null unless the id is a GUID. */
export function selectedClient(): SelectedClient | null {
  const id = cookies.get(CookieNames.clientId);
  return isGuid(id) ? { id, name: cookies.get(CookieNames.clientName) } : null;
}

/** The current client, as the same object while neither cookie has changed (useSyncExternalStore needs that). */
function clientSnapshot(): SelectedClient | null {
  const current = selectedClient();
  if (current?.id !== snapshot?.id || current?.name !== snapshot?.name) {
    snapshot = current;
  }
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** APT244: the selected client, re-rendering the caller whenever setClientId() / selectClient() switches it. */
export function useSelectedClient(): SelectedClient | null {
  return useSyncExternalStore(subscribe, clientSnapshot, clientSnapshot);
}
